import { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import type {
  computePassNetwork,
  computeDefensiveShape,
} from '@4cm/core/tactics/overlays';

/**
 * R3F-native versions of the smaller analytical overlays (PassNet,
 * DefensiveShape, OffsideLine). Same maths as their 2D siblings but
 * rendered as Three.js geometry lying flat on the pitch so the 3D
 * camera projects them with real perspective.
 *
 * Heatmap and PitchControl are intentionally NOT in this file — they
 * sample a 1m grid and would need an InstancedMesh + per-instance
 * colour to perform reasonably; they stay on the 2D SVG overlay for
 * now and are hidden on the 3D board (see TacticsBoard3D).
 */

// Common colour family — matches PlayerToken jersey colours.
const HOME_HEX = 0x4ade80;
const AWAY_HEX = 0xfbbf24;
const NEUTRAL_HEX = 0x4ade80;
const OFFSIDE_HEX = 0xfacc15; // Hawk-Eye yellow

// ---------------------------------------------------------------------------
// PassNet
// ---------------------------------------------------------------------------

export type PassNetLayer3DEdges = ReturnType<typeof computePassNetwork>;

export interface PassNetLayer3DProps {
  edges: PassNetLayer3DEdges;
  opacity: number;
}

// Weight-driven stroke width range — mirrors PassNetLayer2D.
// `computePassNetwork` emits weight = 1 / (1 + distance_metres), so
// the realistic range on a tactics board is roughly (0.05, 0.5]; we
// clamp to keep the thinnest edges legible and the thickest from
// drowning the pitch markings. Bumped to feel more "connected" —
// every edge reads at TV-broadcast scale, the dim ones don't fade
// to invisibility under perspective.
const PASSNET_MIN_W_M = 0.3;
const PASSNET_MAX_W_M = 1.2;
function passNetWidthFor(weight: number): number {
  if (!Number.isFinite(weight) || weight <= 0) return PASSNET_MIN_W_M;
  const w = PASSNET_MIN_W_M + (PASSNET_MAX_W_M - PASSNET_MIN_W_M) * weight;
  if (w < PASSNET_MIN_W_M) return PASSNET_MIN_W_M;
  if (w > PASSNET_MAX_W_M) return PASSNET_MAX_W_M;
  return w;
}

// Node radius is fixed in world metres — broadcast pass nets render
// these as bold dots so the connector terminations read clearly. We
// nudge them slightly larger than the thickest connector so the line
// appears to flow into the node rather than past it.
const PASSNET_NODE_R_M = 1.3;

// Single <line> per edge using drei <Line> (Line2 under the hood) so
// we get genuine variable-thickness lines in world units — the default
// <lineBasicMaterial> can't honour lineWidth > 1 on most platforms.
function PassNetLayer3DInner({ edges, opacity }: PassNetLayer3DProps) {
  // Build edge data (geometry endpoints + colour + width) and the
  // de-duplicated node positions (each player a connector touches gets
  // exactly one disc) in a single pass.
  const { edgeItems, nodes } = useMemo(() => {
    if (!edges || edges.length === 0) {
      return { edgeItems: [], nodes: [] as Array<{ x: number; z: number; color: number; key: string }> };
    }
    const items = edges.map((edge, idx) => {
      const color = edge.team === 'away' ? AWAY_HEX : HOME_HEX;
      const width = passNetWidthFor(edge.weight);
      const points: Array<[number, number, number]> = [
        [edge.from.x, 0.012, edge.from.z],
        [edge.to.x, 0.012, edge.to.z],
      ];
      return { key: idx, points, color, width };
    });
    // De-dup endpoints by rounded position so two edges sharing a
    // player render one disc, not two stacked.
    const seen = new Map<string, { x: number; z: number; color: number; key: string }>();
    for (const edge of edges) {
      const endpoints = [
        { p: edge.from, team: edge.team },
        { p: edge.to, team: edge.team },
      ];
      for (const { p, team } of endpoints) {
        const key = `${Math.round(p.x * 10)},${Math.round(p.z * 10)}`;
        if (!seen.has(key)) {
          seen.set(key, {
            x: p.x,
            z: p.z,
            color: team === 'away' ? AWAY_HEX : HOME_HEX,
            key,
          });
        }
      }
    }
    return { edgeItems: items, nodes: Array.from(seen.values()) };
  }, [edges]);

  if (edgeItems.length === 0) return null;
  return (
    <group raycast={() => undefined} renderOrder={2}>
      {edgeItems.map(({ key, points, color, width }) => (
        <Line
          key={`edge-${key}`}
          points={points}
          color={color}
          // `worldUnits` interprets `lineWidth` in scene metres so the
          // strokes scale with the pitch under perspective foreshortening,
          // matching the broadcast feel where peak connectors are
          // genuinely bigger than the dim ones.
          worldUnits
          lineWidth={width}
          transparent
          opacity={opacity}
          depthWrite={false}
        />
      ))}
      {/* Node discs at each player position the connectors touch — the
          Sky-MNF / Next Gen Stats idiom. Sits just under the lines
          (y=0.011) so the connectors appear to flow INTO each node. */}
      {nodes.map((node) => (
        <mesh
          key={`node-${node.key}`}
          position={[node.x, 0.011, node.z]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <circleGeometry args={[PASSNET_NODE_R_M, 24]} />
          <meshBasicMaterial
            color={node.color}
            transparent
            opacity={opacity}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
    </group>
  );
}

export const PassNetLayer3D = memo(PassNetLayer3DInner);

// ---------------------------------------------------------------------------
// DefensiveShape — polygon hull, one per team
// ---------------------------------------------------------------------------

export type DefensiveShapeHull3D = ReturnType<typeof computeDefensiveShape>;

export interface DefensiveShapeLayer3DProps {
  homeHull: DefensiveShapeHull3D;
  awayHull: DefensiveShapeHull3D;
  opacity: number;
}

function shapeGeometryFor(
  hull: ReadonlyArray<{ x: number; z: number }>,
): THREE.ShapeGeometry | null {
  if (!hull || hull.length < 3) return null;
  const shape = new THREE.Shape();
  // Same coord trick as VoronoiLayer3D: build the Shape in XY,
  // then rotate -π/2 around X so Y maps to world Z. The Y values are
  // negated so the rotation lands the polygon at the correct Z.
  shape.moveTo(hull[0]!.x, -hull[0]!.z);
  for (let i = 1; i < hull.length; i++) {
    shape.lineTo(hull[i]!.x, -hull[i]!.z);
  }
  shape.closePath();
  return new THREE.ShapeGeometry(shape);
}

function DefensiveShapeLayer3DInner({
  homeHull,
  awayHull,
  opacity,
}: DefensiveShapeLayer3DProps) {
  const home = useMemo(() => shapeGeometryFor(homeHull.hull), [homeHull]);
  const away = useMemo(() => shapeGeometryFor(awayHull.hull), [awayHull]);
  if (!home && !away) return null;

  return (
    <group
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0.008, 0]}
      raycast={() => undefined}
      renderOrder={1}
    >
      {home && (
        <mesh geometry={home}>
          <meshBasicMaterial
            color={HOME_HEX}
            transparent
            opacity={opacity}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
      {away && (
        <mesh geometry={away}>
          <meshBasicMaterial
            color={AWAY_HEX}
            transparent
            opacity={opacity}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
    </group>
  );
}

export const DefensiveShapeLayer3D = memo(DefensiveShapeLayer3DInner);

// ---------------------------------------------------------------------------
// OffsideLine — one line per team across the pitch width
// ---------------------------------------------------------------------------

const FIELD_WIDTH_M = 68;

export interface OffsideLineLayer3DProps {
  homeLineZ: number | null;
  awayLineZ: number | null;
  opacity: number;
}

function lineGeomAt(z: number): THREE.BufferGeometry {
  const halfW = FIELD_WIDTH_M / 2;
  const positions = new Float32Array([
    -halfW, 0.014, z,
    halfW, 0.014, z,
  ]);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return g;
}

function OffsideLineLayer3DInner({
  homeLineZ,
  awayLineZ,
  opacity,
}: OffsideLineLayer3DProps) {
  const homeGeom = useMemo(
    () => (homeLineZ === null ? null : lineGeomAt(homeLineZ)),
    [homeLineZ],
  );
  const awayGeom = useMemo(
    () => (awayLineZ === null ? null : lineGeomAt(awayLineZ)),
    [awayLineZ],
  );
  if (!homeGeom && !awayGeom) return null;

  return (
    <group raycast={() => undefined} renderOrder={3}>
      {homeGeom && (
        <lineSegments geometry={homeGeom}>
          <lineBasicMaterial
            color={OFFSIDE_HEX}
            transparent
            opacity={opacity}
            depthWrite={false}
          />
        </lineSegments>
      )}
      {awayGeom && (
        <lineSegments geometry={awayGeom}>
          <lineBasicMaterial
            color={OFFSIDE_HEX}
            transparent
            opacity={opacity}
            depthWrite={false}
          />
        </lineSegments>
      )}
    </group>
  );
}

export const OffsideLineLayer3D = memo(OffsideLineLayer3DInner);

// Re-export NEUTRAL_HEX in case future overlays need the same fallback
// (not part of the API surface — internal callers only).
export const _OVERLAY3D_NEUTRAL_HEX = NEUTRAL_HEX;
